import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { setThemeMode } from '../store/slices/uiSlice';

type Mode = 'light' | 'dark' | 'system';

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function useThemeMode() {
  const mode = useAppSelector((s) => s.ui.themeMode) as Mode;
  const dispatch = useAppDispatch();

  const resolved: 'light' | 'dark' =
    mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode;

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
  }, [resolved]);

  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () =>
      document.documentElement.classList.toggle('dark', mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  return useMemo(
    () => ({ mode, resolved, setMode: (m: Mode) => dispatch(setThemeMode(m)) }),
    [mode, resolved, dispatch]
  );
}
